// 2026-10-14 fix — real gap, reported directly: "all the admin dashboards
// in labarium main admin ... should match the looks ... use the best
// proper one" + explicit follow-up "add dark mode toggle everywhere[,
// every] page." A UI audit confirmed the root cause: no shared header
// component existed, so the navbar/title-strip/back-nav/gradient-opacity/
// spinner-color were independently copy-pasted into 7 separate files, each
// drifting slightly. This extracts that markup ONCE, modeled on
// RcmDashboard.js's navbar (itself modeled on PageShell.js — the most
// brand-coherent, dark-mode-complete implementation found) combined with
// the majority white-title-strip convention (used by 4 of 6 real admin
// dashboards) rather than RCM's own navbar-embedded title treatment.
//
// ThemeToggle is ALWAYS shown here and always flips the real, global MUI
// theme mode (ThemeContext.js) — every page using this shell must give its
// own content (cards/tables/inputs) real isDark-branching, the same
// discipline RcmDashboard.js/RcmCharts.js/RcmPatientPanel.js/
// RcmClinicianPanel.js already use, not just a dark navbar bolted onto an
// otherwise-light page (that half-done state was the actual bug a UI audit
// found and this pass fixes page-by-page).
import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Container, Typography, IconButton, Tooltip } from "@mui/material";
import { motion } from "framer-motion";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LogoutIcon from "@mui/icons-material/Logout";
import ThemeToggle from "./ThemeToggle";

export default function AdminPageShell({ title, subtitle, backPath, backLabel, actions, isDark, onLogout, children }) {
  const navigate = useNavigate();
  // Defaults to the admin session — LabOrdersView.js is the one consumer
  // of this shell shared with the STAFF surface too (not admin-exclusive),
  // so it passes its own onLogout (staff "token"/"/login") instead.
  const handleLogout = onLogout || (() => { localStorage.removeItem("admin_token"); navigate("/admin-login"); });
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: isDark ? "#080d1a" : "#F7F7F7", pb: 8 }}>
      <motion.div initial={{ y: -60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }} style={{ position: "sticky", top: 0, zIndex: 20 }}>
        <Box sx={{
          bgcolor: isDark ? "rgba(2,6,15,0.88)" : "rgba(0,31,77,0.96)",
          backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(201,169,110,0.2)",
          boxShadow: "0 4px 28px rgba(0,31,77,0.2)",
        }}>
          <Box sx={{ height: 2, background: "linear-gradient(90deg, transparent, rgba(201,169,110,0.8) 30%, rgba(223,192,138,0.9) 50%, rgba(201,169,110,0.8) 70%, transparent)" }} />
          <Box sx={{
            maxWidth: 1200, mx: "auto", px: 3, py: 1, minHeight: 60,
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1,
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {backPath && (
                <>
                  <Box onClick={() => navigate(backPath)} sx={{
                    display: "flex", alignItems: "center", gap: 1, color: "rgba(255,255,255,0.55)", fontSize: 14.5,
                    cursor: "pointer", borderRadius: "8px", px: 1.5, py: 0.7, transition: "all 0.2s",
                    "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
                  }}>
                    <ArrowBackIcon sx={{ fontSize: 15 }} />
                    <span>{backLabel || "Admin"}</span>
                  </Box>
                  <Box sx={{ width: "1px", height: 22, bgcolor: "rgba(255,255,255,0.12)" }} />
                </>
              )}
              <img src="/logo-labarium-dark.png" alt="Labarium" style={{ height: 38, width: "auto" }} />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ThemeToggle />
              <Tooltip title="Log out">
                <IconButton size="small" onClick={handleLogout}
                  sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" } }}>
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </motion.div>

      <Box sx={{
        backgroundColor: isDark ? "#0b1220" : "#FFF",
        borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #E0E0E0",
        px: { xs: 3, md: 6 }, py: 2.5, mb: 4,
      }}>
        <Container maxWidth="md" disableGutters sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.4rem", color: isDark ? "#f1f5f9" : "#111", letterSpacing: "-0.3px" }}>
              {title}
            </Typography>
            {subtitle && <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#888" }}>{subtitle}</Typography>}
          </Box>
          {actions && <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>{actions}</Box>}
        </Container>
      </Box>

      {children}
    </Box>
  );
}
